import {OnDestroy, OnInit} from '@angular/core';
import {L10nLocale, L10nTranslationService} from 'angular-l10n';
import {Subscription} from 'rxjs';

interface LanguageDecoratorTargetInstance extends Partial<OnInit>, Partial<OnDestroy> {
    translationService: L10nTranslationService;
}

type LanguageDecoratorTarget = Partial<OnInit> & Partial<OnDestroy>;

type LanguageDecorator = (target: LanguageDecoratorTarget, propertyKey: string | symbol) => void;

/**
 * Property decorator for components to provide the parameter to the translate pipe.
 */
export function Language(): LanguageDecorator {

    function DecoratorFactory(
        target: LanguageDecoratorTarget,
        propertyKey?: string | symbol,
        descriptor?: PropertyDescriptor
    ): void {
        let subscription: Subscription;

        const targetNgOnInit: () => void = target.ngOnInit;

        function ngOnInit(this: LanguageDecoratorTargetInstance): void {
            const translationService: L10nTranslationService = this.translationService;

            if (typeof propertyKey !== 'undefined') {

                // When the language changes, subscribes to the event & updates language property.
                subscription = translationService.onChange().subscribe(
                    (locale: L10nLocale) => {
                        if (descriptor) {
                            if (typeof descriptor.value === 'function') {
                                (descriptor.value as (lang: string) => void).call(this, locale.language);
                            } else {
                                descriptor.value = locale.language;
                            }
                        } else {
                            (this as any)[propertyKey] = locale.language;
                        }
                    });
            }

            if (targetNgOnInit) {
                targetNgOnInit.apply(this);
            }
        }

        target.ngOnInit = ngOnInit;

        const targetNgOnDestroy: () => void = target.ngOnDestroy;

        function ngOnDestroy(this: any): void {
            if (typeof subscription !== 'undefined') {
                subscription.unsubscribe();
            }

            if (targetNgOnDestroy) {
                targetNgOnDestroy.apply(this);
            }
        }

        target.ngOnDestroy = ngOnDestroy;

        if (typeof propertyKey !== 'undefined') {
            Object.defineProperty(target, propertyKey, {
                writable: true,
                value:    undefined
            });
        }
    }

    return DecoratorFactory;

}
